// ==================== PastAttendanceModal.jsx ====================
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import "../Styling/PastAttendanceModal.css";
import { createPortal } from "react-dom";

const PastAttendanceModal = ({ isOpen, onClose, userId }) => {
  const { user } = useAuth();
  const orgId = user?.organisationId;

  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [attendance, setAttendance] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [leaves, setLeaves] = useState({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= Helpers ================= */

  const isWeekend = (dateStr) => {
    const day = new Date(dateStr).getDay();
    return day === 0 || day === 6;
  };

  const isFutureDate = (dateStr) => {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);

    const t = new Date();
    t.setHours(0, 0, 0, 0);

    return d > t;
  };

  const allMonths = [
    { label: "January", value: 1 },
    { label: "February", value: 2 },
    { label: "March", value: 3 },
    { label: "April", value: 4 },
    { label: "May", value: 5 },
    { label: "June", value: 6 },
    { label: "July", value: 7 },
    { label: "August", value: 8 },
    { label: "September", value: 9 },
    { label: "October", value: 10 },
    { label: "November", value: 11 },
    { label: "December", value: 12 },
  ];

  const years = Array.from({ length: 10 }, (_, i) => today.getFullYear() - i);

  const months =
    year === today.getFullYear()
      ? allMonths.filter((m) => m.value <= today.getMonth() + 1)
      : allMonths;

  /* ================= Fetch Monthly Attendance ================= */

  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchMonthlyAttendance = async () => {
      setLoading(true);
      setError("");
      setAttendance([]);

      try {
        const res = await fetch(
          `${Base_URL}/Attendance/GetMonthlyAttendance?userId=${userId}&year=${year}&month=${month}`
        );

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setAttendance(
          Array.isArray(data)
            ? data.map((item) => ({
                date: item.date,
                status: item.status,
                hours: item.hours,
              }))
            : []
        );
      } catch (err) {
        console.error("❌ Monthly attendance error:", err);
        setError("Failed to load attendance");
      } finally {
        setLoading(false);
      }
    };

    fetchMonthlyAttendance();
  }, [isOpen, userId, year, month]);

  /* ================= Fetch Holidays ================= */

  useEffect(() => {
    if (!orgId || !isOpen) return;

    const fetchHolidays = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/HolidayMasterOrg/GetHolidaysByOrg/${orgId}`
        );
        const data = await res.json();

        const holidayMap = {};
        data
          .filter(
            (h) =>
              h.holidayType === "National" ||
              h.holidayType === "Festival"
          )
          .forEach((h) => {
            holidayMap[new Date(h.holidayDate).toDateString()] =
              h.holidayName;
          });

        setHolidays(holidayMap);
      } catch (err) {
        console.error("❌ Holiday fetch error:", err);
      }
    };

    fetchHolidays();
  }, [orgId, isOpen]);

  /* ================= Fetch Past Leaves ================= */

  useEffect(() => {
    if (!isOpen || !userId) return;

    const fetchPastLeaves = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/Leave/past-leaves/${userId}`
        );
        const data = await res.json();

        const leaveMap = {};

        if (Array.isArray(data)) {
          data.forEach((leave) => {
            const from = new Date(leave.fromDate);
            const to = new Date(leave.toDate);

            for (
              let d = new Date(from);
              d <= to;
              d.setDate(d.getDate() + 1)
            ) {
              leaveMap[d.toDateString()] =
                leave.leaveType || "Leave";
            }
          });
        }

        setLeaves(leaveMap);
      } catch (err) {
        console.error("❌ Leave fetch error:", err);
      }
    };

    fetchPastLeaves();
  }, [isOpen, userId]);

  /* ================= CALCULATIONS (Future days excluded) ================= */

  const validDays = attendance.filter(
    (day) => !isFutureDate(day.date)
  );

  const leaveCount = validDays.filter((day) => {
    const key = new Date(day.date).toDateString();
    return leaves[key];
  }).length;

  const workingDays = validDays.filter((day) => {
    const key = new Date(day.date).toDateString();
    return (
      !isWeekend(day.date) &&
      !holidays[key] &&
      !leaves[key]
    );
  });

  const presentCount = workingDays.filter(
    (day) =>
      day.status?.toLowerCase() === "present" ||
      day.status?.toLowerCase() === "wfh"
  ).length;

  const absentCount = workingDays.length - presentCount;

  if (!isOpen) return null;

  /* ================= UI ================= */

  return  createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header-attendance">
          <h3 >Monthly Attendance</h3>
          <X className="close-icon" onClick={onClose} />
        </div>

        {/* Filters */}
        <div className="filters">
          <select value={year} onChange={(e) => setYear(+e.target.value)}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          <select value={month} onChange={(e) => setMonth(+e.target.value)}>
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Attendance Grid */}
        <div className="attendance-strip modal-strip">
          {loading && <p className="modal-msg">Loading...</p>}
          {!loading && error && (
            <p className="modal-msg error">{error}</p>
          )}
          {!loading && !error && attendance.length === 0 && (
            <p className="modal-msg">No attendance found</p>
          )}

          {!loading &&
            !error &&
            attendance.map((day, idx) => {
              const key = new Date(day.date).toDateString();
              const isHoliday = holidays[key];
              const isLeave = leaves[key];
              const isWeekOff = isWeekend(day.date);
              const isFuture = isFutureDate(day.date);

              let pillClass = day.status?.toLowerCase();
              let pillText = day.hours;
              let pillTitle = `${day.date} • ${day.status} • ${day.hours}`;

              if (isHoliday) {
                pillClass = "holiday";
                pillText = "Holiday";
                pillTitle = `Holiday • ${holidays[key]}`;
              } else if (isLeave) {
                pillClass = "leave";
                pillText = "Leave";
                pillTitle = `Leave • ${leaves[key]}`;
              } else if (isWeekOff) {
                pillClass = "weekoff";
                pillText = "Off";
                pillTitle = "Week Off";
              }

              return (
                <div
                  key={idx}
                  className={`attendance-pill ${pillClass} ${
                    isFuture ? "future-day" : ""
                  }`}
                  title={isFuture ? "Future Date" : pillTitle}
                >
                  <span className="pill-date">
                    {new Date(day.date).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </span>
                  <span className="pill-hours">{pillText}</span>
                </div>
              );
            })}
        </div>

        {/* Summary (Holiday REMOVED) */}
        <div className="attendance-legend modal-legend">
          <span>
            <i className="dot present"></i> Present: {presentCount}
          </span>
          <span>
            <i className="dot absent"></i> Absent: {absentCount}
          </span>
          <span>
            <i className="dot leave"></i> Leave: {leaveCount}
          </span>
          <span>
            <i className="dot weekoff"></i> Week Off
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PastAttendanceModal;
